import React, { useRef, useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { FormattedMessage, useIntl } from 'react-intl';
import cartonLogo from '../../assets/images/Logo_Cartondotcom.png';

// Imported components and styling libraries
import { Box, Grid, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { get, post, deleteData } from 'utils/axiosRequests';

// Import logo
import mainLogo from '../../assets/images/pdf-logo.jpeg';
import PrimaryButton from 'ui-component/custom-buttons/PrimaryButton';
import { useLocation } from 'react-router-dom';
import pdfIcon from '../../assets/images/icons/pdf-icon.svg';
import CircularProgress from '@mui/material/CircularProgress';
// Styled components with black and white theme
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    boxShadow: 'none',
    borderRadius: 0,
    backgroundColor: '#fff',
    width: '100%',
    maxWidth: '100%',
    margin: 0,
    border: 'none',
    textAlign: 'left' // Add this to ensure left alignment
    // '& *': {
    //     textAlign: 'inherit' // Ensure all children inherit the left alignment
    // }
}));

// const HeaderBox = styled(Box)(({ theme }) => ({
//     display: 'flex',
//     flexDirection: 'column',
//     justifyContent: 'center',
//     alignItems: 'center',
//     marginBottom: theme.spacing(2),
//     paddingBottom: theme.spacing(1),
//     borderBottom: `1px solid ${theme.palette.divider}`
// }));
const HeaderBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(3)
}));

const TopSection = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    width: '100%'
});

const InfoRow = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: '1px solid #ddd',
    paddingTop: '20px'
});

const LogoRow = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: '30px'
});

const EstimationRow = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    marginBottom: '10px'
});

// const InfoRow = styled(Box)({
//     display: 'flex',
//     justifyContent: 'space-between',
//     borderTop: '1px solid #ddd',
//     paddingTop: '20px'
// });
const formatCurrentDate = () => {
    const now = new Date();
    const months = [
        'january',
        'february',
        'march',
        'april',
        'may',
        'june',
        'july',
        'august',
        'september',
        'october',
        'november',
        'december'
    ];
    return `date: ${months[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;
};
const SectionTitle = styled(Typography)(({ theme }) => ({
    color: '#000000',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    fontSize: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
}));

const StyledTableCell = styled(TableCell)(({ theme, header }) => ({
    ...(header && {
        backgroundColor: '#000000',
        color: '#ffffff',
        fontWeight: 700,
        fontSize: '1rem'
    }),
    fontSize: '0.95rem'
}));

const PropertyText = styled(Typography)({
    fontSize: '1rem',
    marginBottom: '6px'
});

const SpecRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: '8px 0',
    '&:last-child': {
        borderBottom: 'none'
    }
}));

const SpecLabel = styled(Typography)({
    fontWeight: 500,
    width: '200px',
    flexShrink: 0
});

const SpecValue = styled(Typography)({
    flexGrow: 1
});

const SectionBox = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    // border: `1px solid ${theme.palette.divider}`,
    border: 'none',
    borderRadius: 0,
    backgroundColor: '#fff'
}));

const NotesBox = styled(Box)(({ theme }) => ({
    // backgroundColor: theme.palette.grey[50],
    backgroundColor: '#fff',
    // borderRadius: theme.shape.borderRadius,
    borderRadius: '0',
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    // border: `1px solid ${theme.palette.divider}`
    border: `none`
}));

const ProfessionalEstimatePDF = ({ id }) => {
    const reportRef = useRef(null);
    const localLanguage = localStorage.getItem('berry-config') ? JSON.parse(localStorage.getItem('berry-config')).locale : 'en';
    const intl = useIntl();
    const [leadDetails, setLeadDetails] = useState(undefined);
    const [termConditionsData, setTermConditionsData] = useState(null);
    const currentRoute = useLocation();
    const [isGenerating, setIsGenerating] = useState(false);

    // eslint-disable-next-line arrow-body-style
    const getItemsDetails = () => {
        return get(`/api/items/${id}`).then((res) => {
            setLeadDetails(res?.data);
            return res?.data;
        });
    };

    // Replace existing getTermConditions function
    // eslint-disable-next-line arrow-body-style
    const getTermConditions = () => {
        return get(`/api/terms-and-condition`).then((res) => {
            setTermConditionsData(res.data);
            return res.data;
        });
    };

    // useEffect(() => {
    //     getItemsDetails();
    //     getTermConditions();
    // }, []);

    // Generate PDF function
    const generatePDF = async () => {
        if (!reportRef.current) return;
        setIsGenerating(true);

        try {
            let currentLeadDetails = leadDetails;
            let currentTermConditions = termConditionsData;

            // Fetch data if not already available
            if (!currentLeadDetails) {
                currentLeadDetails = await getItemsDetails();
            }
            if (!currentTermConditions) {
                currentTermConditions = await getTermConditions();
            }

            // Wait for state to update and DOM to render
            await new Promise((resolve) => setTimeout(resolve, 200));

            // Optimized html2canvas options
            const canvas = await html2canvas(reportRef.current, {
                scale: 2, // adjust scaling for quality of pdf
                useCORS: true,
                allowTaint: false,
                backgroundColor: '#ffffff',
                removeContainer: true,
                imageTimeout: 0,
                logging: false // Disable logging for better performance
            });

            // Convert to JPEG with compression instead of PNG
            const imgData = canvas.toDataURL('image/jpeg', 0.7); // 70% quality JPEG

            // Create PDF with compression enabled
            const pdf = new JsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true,
                precision: 2
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const contentWidth = pdfWidth - margin * 2;
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;

            const pageHeightInPx = (canvas.width / contentWidth) * (pdfHeight - margin * 2);
            let renderedHeight = 0;
            let pageNumber = 0;

            while (renderedHeight < canvas.height) {
                const pageCanvas = document.createElement('canvas');
                pageCanvas.width = canvas.width;
                pageCanvas.height = Math.min(pageHeightInPx, canvas.height - renderedHeight);

                const pageCtx = pageCanvas.getContext('2d');
                if (pageCtx) {
                    // Set white background
                    pageCtx.fillStyle = 'white';
                    pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

                    // Draw the page content
                    pageCtx.drawImage(canvas, 0, renderedHeight, canvas.width, pageCanvas.height, 0, 0, canvas.width, pageCanvas.height);
                }

                // Convert page to JPEG with compression
                const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.7);

                if (pageNumber > 0) pdf.addPage();

                const pageContentHeight = (contentWidth * pageCanvas.height) / pageCanvas.width;

                // Add image as JPEG to PDF
                pdf.addImage(pageImgData, 'JPEG', margin, margin, contentWidth, pageContentHeight);

                renderedHeight += pageHeightInPx;
                pageNumber += 1;
            }

            // Generate filename using the fetched data
            const estimationRid = currentLeadDetails?.estimationRid || 'RID-QUOTE';
            // Save with the proper filename
            pdf.save(`${estimationRid}.pdf`);
        } catch (error) {
            console.error('PDF generation failed:', error);
        } finally {
            setIsGenerating(false);
        }
    };

    // Also add this helper function to optimize the logo
    const optimizeLogo = async (logoSrc) =>
        new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Set maximum dimensions for logo
                const maxWidth = 400;
                const maxHeight = 200;

                let { width, height } = img;

                // Calculate new dimensions maintaining aspect ratio
                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }

                if (height > maxHeight) {
                    width = (width * maxHeight) / height;
                    height = maxHeight;
                }

                canvas.width = width;
                canvas.height = height;

                // Draw resized image
                ctx.drawImage(img, 0, 0, width, height);

                // Convert to JPEG with compression
                const optimizedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
                resolve(optimizedDataUrl);
            };

            img.onerror = () => resolve(logoSrc); // Fallback to original
            img.src = logoSrc;
        });

    // Helper function for localized text
    const getLocalizedText = (enText, esText) => (localLanguage === 'es' ? esText : enText);

    // Get customer info safely
    const customerCompany = leadDetails?.itemDetails?.assignCustomerDetails?.[0]?.customerDetails?.company_name || '';
    const customerFirstName = leadDetails?.itemDetails?.assignCustomerDetails?.[0]?.customerDetails?.socialLeadDetails?.firstName || '';
    const customerLastName = leadDetails?.itemDetails?.assignCustomerDetails?.[0]?.customerDetails?.socialLeadDetails?.lastName || '';
    const customerAddress = leadDetails?.itemDetails?.assignCustomerDetails?.[0]?.customerDetails?.delivery_address || '';
    const customerEmail = leadDetails?.itemDetails?.assignCustomerDetails?.[0]?.customerDetails?.emailAddress || '';

    // Generate estimate number with leading zeros
    const estimateNumber = `EST-${String(leadDetails?.id || '0').padStart(5, '0')}`;
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    return (
        <>
            {currentRoute.pathname === '/potential-cartons' ? (
                <IconButton
                    color="primary"
                    onClick={generatePDF}
                    disabled={isGenerating}
                    sx={{
                        // mb: 2,
                        width: { xs: '100%', sm: 'auto' },
                        // padding: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    {isGenerating ? (
                        <CircularProgress size={24} />
                    ) : (
                        <img src={pdfIcon} alt="Download PDF" style={{ width: 24, height: 24 }} />
                        // <IconButton>
                        //     <PictureAsPdfIcon size={24} />
                        // </IconButton>
                    )}
                </IconButton>
            ) : (
                <PrimaryButton
                    variant="contained"
                    color="primary"
                    onClick={generatePDF}
                    disabled={isGenerating}
                    sx={{
                        width: { xs: '100%', sm: 'auto' },
                        mb: 2
                    }}
                >
                    {isGenerating ? <CircularProgress size={24} /> : <FormattedMessage id="Generate Quote" />}
                </PrimaryButton>
            )}

            <Box
                ref={reportRef}
                sx={{
                    position: 'absolute',
                    left: '-9999px',
                    width: '794px',
                    padding: 0,
                    margin: 0,
                    backgroundColor: '#fff',
                    textAlign: 'left',
                    '& *': {
                        textAlign: 'inherit' // Add this to ensure all children inherit left alignment
                    },
                    // Override any center alignment that might be inherited
                    '& .MuiTypography-root': {
                        textAlign: 'left'
                    },
                    // Except for specifically right-aligned elements
                    '& .MuiTypography-root[sx*="textAlign: \'right\'"]': {
                        textAlign: 'right'
                    }
                }}
            >
                <StyledPaper elevation={0}>
                    <HeaderBox>
                        {/* Top Section with Logo and Estimation */}
                        <TopSection>
                            {/* Logo */}
                            <Box display="flex" alignItems="center">
                                <img src={cartonLogo} alt="Carton Logo" style={{ maxWidth: '400px', height: '35px' }} />
                            </Box>

                            {/* quotation Info */}
                            <Box sx={{ textAlign: 'right' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end'
                                    }}
                                >
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            color: '#000000',
                                            fontWeight: 700,
                                            fontSize: '2.4rem',
                                            textTransform: 'lowercase',
                                            letterSpacing: '-1px',
                                            lineHeight: 1
                                        }}
                                    >
                                        <FormattedMessage id="quotation" />
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#2e3191',
                                            fontWeight: 700,
                                            fontSize: '1rem',
                                            mt: 0.8,
                                            textTransform: 'uppercase !important'
                                        }}
                                    >
                                        {leadDetails?.estimationRid}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#666', fontSize: '1.1rem', mt: 0.5, textAlign: 'right' }}>
                                        <span style={{ fontWeight: 800 }}>
                                            {' '}
                                            <FormattedMessage id="Date" />
                                        </span>{' '}
                                        : {today}
                                    </Typography>
                                </Box>
                            </Box>
                        </TopSection>

                        {/* Customer and Our Information */}
                        <InfoRow>
                            {/* Customer Information */}
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: '#000',
                                        fontWeight: 700,
                                        fontSize: '1.5rem',
                                        mb: 2,
                                        textTransform: 'lowercase'
                                    }}
                                >
                                    <FormattedMessage id="customer information" />
                                </Typography>
                                <PropertyText variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                                    carton
                                </PropertyText>
                                <PropertyText variant="body1" sx={{ fontSize: '1.1rem', color: '#444' }}>
                                    123 packaging boulevard
                                </PropertyText>
                                <PropertyText variant="body1" sx={{ fontSize: '1.1rem', color: '#444' }}>
                                    carton city, ca 12345
                                </PropertyText>
                                <PropertyText variant="body1" sx={{ fontSize: '1.1rem', color: '#444' }}>
                                    +1 (555) 123-4567
                                </PropertyText>
                                <PropertyText variant="body1" sx={{ fontSize: '1.1rem', color: '#444' }}>
                                    sales@cartoncompany.com
                                </PropertyText>
                            </Box>

                            {/* Spacer */}
                            <Box sx={{ width: '80px' }} />

                            {/* Our Information */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: '#000',
                                        fontWeight: 700,
                                        fontSize: '1.5rem',
                                        mb: 2,
                                        textTransform: 'lowercase'
                                    }}
                                >
                                    <FormattedMessage id="our information" />
                                </Typography>
                                <PropertyText variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                                    carton company inc.
                                </PropertyText>
                                <PropertyText variant="body1" sx={{ fontSize: '1.1rem', color: '#444', textAlign: 'right' }}>
                                    123 packaging boulevard
                                </PropertyText>
                                <PropertyText variant="body1" sx={{ fontSize: '1.1rem', color: '#444', textAlign: 'right' }}>
                                    carton city, ca 12345
                                </PropertyText>
                                <PropertyText variant="body1" sx={{ fontSize: '1.1rem', color: '#444', textAlign: 'right' }}>
                                    +1 (555) 123-4567
                                </PropertyText>
                                <PropertyText variant="body1" sx={{ fontSize: '1.1rem', color: '#444', textAlign: 'right' }}>
                                    sales@cartoncompany.com
                                </PropertyText>
                            </Box>
                        </InfoRow>
                    </HeaderBox>
                    {/* Customer and Company Information */}
                    {/* <Grid container spacing={3} sx={{ mb: 4 }}>
                       
                        <Grid item xs={6}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12}>
                                    <SectionTitle variant="h6">Customer Information</SectionTitle>
                                    <PropertyText variant="body1" fontWeight="bold">
                                        {customerCompany || 'Carton'}
                                    </PropertyText>
                                    <PropertyText variant="body1">
                                        {customerFirstName} {customerLastName}
                                    </PropertyText>
                                    <PropertyText variant="body1">{customerAddress}</PropertyText>
                                    <PropertyText variant="body1">{customerEmail}</PropertyText>
                                </Grid>
                            </Grid>
                        </Grid>

                        {
                        <Grid item xs={6}>
                            <SectionTitle variant="h6">Our Information</SectionTitle>
                            <PropertyText variant="body1" fontWeight="bold">
                                Carton Company Inc.
                            </PropertyText>
                            <PropertyText variant="body1">123 Packaging Boulevard</PropertyText>
                            <PropertyText variant="body1">Carton City, CA 12345</PropertyText>
                            <PropertyText variant="body1">+1 (555) 123-4567</PropertyText>
                            <PropertyText variant="body1">sales@cartoncompany.com</PropertyText>
                        </Grid>
                    </Grid> */}

                    {/* Item Name Section */}

                    <SectionBox sx={{ backgroundColor: '#fff' }}>
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                border: 'none',
                                boxShadow: 'none'
                            }}
                        >
                            <Table
                                sx={{
                                    borderCollapse: 'collapse',
                                    '& td, & th': {
                                        border: 'none !important'
                                    },
                                    // Target all borders in the table header specifically
                                    '& thead tr th': {
                                        borderBottom: 'none !important'
                                    },
                                    // Remove any divider lines that might be coming from TableCell
                                    '& .MuiTableCell-root': {
                                        borderBottom: 'none !important'
                                    }
                                }}
                            >
                                <TableHead>
                                    <TableRow sx={{ border: 'none' }}>
                                        <StyledTableCell
                                            header
                                            style={{
                                                color: 'white',
                                                border: 'none'
                                            }}
                                        >
                                            <FormattedMessage id="Item Name" />
                                        </StyledTableCell>
                                        <StyledTableCell
                                            header
                                            align="center"
                                            style={{
                                                color: 'white',
                                                border: 'none'
                                            }}
                                        >
                                            <FormattedMessage id="minimum order" />
                                        </StyledTableCell>
                                        <StyledTableCell
                                            header
                                            align="center"
                                            style={{
                                                color: 'white',
                                                border: 'none'
                                            }}
                                        >
                                            <FormattedMessage id="price per unit" />
                                        </StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow sx={{ border: 'none' }}>
                                        <TableCell sx={{ border: 'none' }}>
                                            <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                                {leadDetails?.itemName || '(Example: B-type corrugated box)'}
                                            </Typography>

                                            <Box sx={{ mt: 1 }}>
                                                <PropertyText variant="body1">
                                                    <strong>
                                                        <FormattedMessage id="Style" />:
                                                    </strong>{' '}
                                                    {getLocalizedText(
                                                        leadDetails?.itemDetails?.styleDetails?.styleEn || '-',
                                                        leadDetails?.itemDetails?.styleDetails?.styleEs || '-'
                                                    )}
                                                </PropertyText>

                                                <PropertyText variant="body1">
                                                    <strong>
                                                        <FormattedMessage id="Material" />:
                                                    </strong>{' '}
                                                    {getLocalizedText(
                                                        leadDetails?.itemDetails?.enduranceDetails?.enduranceEn || '-',
                                                        leadDetails?.itemDetails?.enduranceDetails?.enduranceEs || '-'
                                                    )}
                                                </PropertyText>

                                                <PropertyText variant="body1">
                                                    <strong>
                                                        <FormattedMessage id="Dimensions" />:
                                                    </strong>{' '}
                                                    {leadDetails?.length || '0'} × {leadDetails?.width || '0'} ×{' '}
                                                    {leadDetails?.height || '0'} (L × W × H)
                                                </PropertyText>

                                                {leadDetails?.itemDetails?.printType && (
                                                    <PropertyText variant="body1">
                                                        <strong>Print Type:</strong> {leadDetails?.itemDetails?.printType}
                                                    </PropertyText>
                                                )}

                                                {leadDetails?.itemDetails?.colorQuantity && (
                                                    <PropertyText variant="body1">
                                                        <strong>Colors:</strong> {leadDetails?.itemDetails?.colorQuantity}
                                                    </PropertyText>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontSize: '1rem', fontWeight: 500, border: 'none' }}>
                                            {leadDetails?.minimumOrder || '-'}
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontSize: '1rem', fontWeight: 500, border: 'none' }}>
                                            {leadDetails?.pricePerUnit || '-'}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SectionBox>
                    {/* Key Specifications Section */}
                    <SectionBox>
                        <SectionTitle variant="h5">
                            <Box component="span" sx={{ color: '#2ecc71', fontSize: '1.5rem' }}>
                                🔎
                            </Box>{' '}
                            <FormattedMessage id="Key Specifications" />
                        </SectionTitle>

                        <Box sx={{ ml: 3 }}>
                            {[
                                // { label: {intl.formatMessage({ id: 'Type' })}, value: leadDetails?.type },
                                { label: 'Style', value: leadDetails?.itemDetails?.styleDetails?.styleEn },
                                { label: 'Material', value: leadDetails?.itemDetails?.enduranceDetails?.enduranceEn },
                                { label: 'Flute', value: leadDetails?.flute },
                                { label: 'Liner', value: leadDetails?.liner },
                                { label: 'Liner Coating', value: leadDetails?.linerCoating },
                                { label: 'Market', value: leadDetails?.industry },
                                { label: 'Number of Inks', value: leadDetails?.numberOfInks },
                                { label: 'Gluing', value: leadDetails?.itemDetails?.gluedDetails?.gluedEn },
                                { label: 'ECT (Edge Crush Test)', value: leadDetails?.ect },
                                { label: 'Length (mm)', value: leadDetails?.length },
                                { label: 'Width (mm)', value: leadDetails?.width },
                                { label: 'Height (mm)', value: leadDetails?.height }
                            ].map((spec, index) => (
                                <SpecRow key={index}>
                                    <SpecLabel variant="body1">{spec.label}</SpecLabel>
                                    <SpecValue variant="body1">{spec.value || '-'}</SpecValue>
                                </SpecRow>
                            ))}
                        </Box>
                    </SectionBox>

                    {/* Pricing Table */}
                    {/* Additional Costs Section */}
                    <SectionBox sx={{ mt: 8 }}>
                        <SectionTitle variant="h5">
                            <Box component="span" sx={{ color: '#4caf50', fontSize: '1.5rem' }}>
                                💲
                            </Box>{' '}
                            additional costs
                        </SectionTitle>

                        <Grid container spacing={1} sx={{ ml: 1 }}>
                            {/* Die-cut Cost Field */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                mb: 1,
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            incluye costo de suaje
                                        </Typography>
                                        <TextField
                                            variant="outlined"
                                            value={leadDetails?.itemDetails?.corrugatedDirectionDetails?.directionEn || ''}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'white',
                                                    borderRadius: 2,
                                                    // '& fieldset': {
                                                    //     borderColor: '#ddd'
                                                    // },
                                                    // '&:hover fieldset': {
                                                    //     borderColor: '#999'
                                                    // },
                                                    // '&.Mui-focused fieldset': {
                                                    //     borderColor: '#4caf50'
                                                    // },
                                                    height: '35px'
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Engraving Cost Field */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#999',
                                                mb: 1,
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            incluye costo de grabado
                                        </Typography>
                                        <TextField
                                            variant="outlined"
                                            value={leadDetails?.itemDetails?.EngravingCost?.engravingCostEn || ''}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'white',
                                                    borderRadius: 2,
                                                    // '& fieldset': {
                                                    //     borderColor: '#ddd'
                                                    // },
                                                    // '&:hover fieldset': {
                                                    //     borderColor: '#999'
                                                    // },
                                                    // '&.Mui-focused fieldset': {
                                                    //     borderColor: '#4caf50'
                                                    // },
                                                    height: '35px'
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Shipping Cost Field */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#999',
                                                mb: 1,
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            incluye envío a su planta
                                        </Typography>
                                        <TextField
                                            variant="outlined"
                                            value={leadDetails?.itemDetails?.ShippingCost?.shippingCostEn || ''}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'white',
                                                    borderRadius: 2,
                                                    // '& fieldset': {
                                                    //     borderColor: '#ddd'
                                                    // },
                                                    // '&:hover fieldset': {
                                                    //     borderColor: '#999'
                                                    // },
                                                    // '&.Mui-focused fieldset': {
                                                    //     borderColor: '#4caf50'
                                                    // },
                                                    height: '35px'
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </SectionBox>

                    {/* Delivery Details Section */}
                    <SectionBox>
                        <SectionTitle variant="h5">
                            <Box component="span" sx={{ color: '#e74c3c', fontSize: '1.5rem' }}>
                                🚚
                            </Box>{' '}
                            <FormattedMessage id="Delivery Details" />
                        </SectionTitle>

                        <Box sx={{ ml: 2 }}>
                            <SpecRow>
                                <SpecLabel variant="body1">
                                    <FormattedMessage id="Country" />
                                </SpecLabel>
                                <SpecValue variant="body1">{leadDetails?.itemDetails?.countryDetails?.countryName || '-'}</SpecValue>
                            </SpecRow>
                            <SpecRow>
                                <SpecLabel variant="body1">
                                    <FormattedMessage id="State" />
                                </SpecLabel>
                                <SpecValue variant="body1">{leadDetails?.itemDetails?.stateDetails?.stateName || '-'}</SpecValue>
                            </SpecRow>
                            <SpecRow>
                                <SpecLabel variant="body1">
                                    <FormattedMessage id="Delivery City" />
                                </SpecLabel>
                                <SpecValue variant="body1">{leadDetails?.itemDetails?.DeliveryCity?.cityEn || '-'}</SpecValue>
                            </SpecRow>
                            <SpecRow>
                                <SpecLabel variant="body1">
                                    <FormattedMessage id="Postal Code" />
                                </SpecLabel>
                                <SpecValue variant="body1">{leadDetails?.boardingArea || '-'}</SpecValue>
                            </SpecRow>
                        </Box>
                    </SectionBox>

                    {/* Notes & Terms */}
                    <NotesBox>
                        <SectionTitle variant="h6">
                            <FormattedMessage id="Notes & Terms" />
                        </SectionTitle>

                        {Array.isArray(termConditionsData) &&
                            termConditionsData.map((item, index) => (
                                <Typography
                                    key={item.id || index}
                                    variant="body1"
                                    sx={{ mb: 1, fontSize: '0.95rem' }}
                                    dangerouslySetInnerHTML={{
                                        __html: getLocalizedText(item.textEn || '-', item.textEs || '-')
                                    }}
                                />
                            ))}

                        {/* <Typography variant="body1" sx={{ fontSize: '0.95rem' }}>
                            • Minimum order quantity: {leadDetails?.itemDetails?.minimumOrder || 'Contact for details'}
                        </Typography> */}
                    </NotesBox>

                    {/* Footer */}
                    <Box sx={{ mt: 4, pt: 1.5, borderTop: '1px solid rgba(0, 0, 0, 0.2)', textAlign: 'center' }}>
                        {' '}
                        {/* Changed mt from 5 to 3, pt from 2 to 1.5 */}
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem', textAlign: 'center' }}>
                            <FormattedMessage id="Thank you for choosing Carton Company for your packaging needs." />
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem', textAlign: 'center' }}>
                            <FormattedMessage id="For questions about this estimate, please contact your account manager." />
                        </Typography>
                    </Box>
                </StyledPaper>
            </Box>
        </>
    );
};

export default ProfessionalEstimatePDF;
